/**
 * The live-state projection and the WebSocket contract Phase 2 consumes.
 *
 * Both the server that writes this and the client that renders it import this file.
 * A live map is the one surface where a shape mismatch is invisible in testing and
 * obvious to a customer: the server renames a field, the client reads undefined, and
 * every marker quietly stops moving while both sides report healthy.
 *
 * INVARIANT 7 — THE PROJECTION REFUSES TO MOVE BACKWARDS. Records arrive out of order
 * constantly: a device leaving a tunnel flushes ten minutes of buffered positions
 * *after* the live one, and a stream redelivery replays a record already applied.
 * Writing whatever arrived last walks the marker back down the road and then forward
 * again, which reads as a GPS fault to the person watching. Writes go through
 * `LIVE_STATE_CAS_SCRIPT`, which compares the stored timestamp inside Redis; the
 * comparison cannot be done in the application because two decoders race.
 */
/** A vehicle silent this long is shown as losing its link, but not yet as offline. */
export const LIVE_STALE_AFTER_MS = 3 * 60_000;
/** Past this the vehicle is offline. Roughly six missed one-minute reports. */
export const LIVE_OFFLINE_AFTER_MS = 10 * 60_000;
/**
 * Connectivity at read time, from the newest timestamp the platform has for a device.
 *
 * This is the ONLY place 'offline' is produced. `lastSeenAtMs` is `devices.last_seen_at`
 * (or the projection's own `ts`), never a stored state value.
 */
export function liveConnectivity(lastSeenAtMs, nowMs, options = {}) {
    if (lastSeenAtMs === null || !Number.isFinite(lastSeenAtMs))
        return 'offline';
    const staleAfterMs = options.staleAfterMs ?? LIVE_STALE_AFTER_MS;
    const offlineAfterMs = options.offlineAfterMs ?? LIVE_OFFLINE_AFTER_MS;
    // Clock skew and buffered flushes both produce a "future" last-seen. Treating it as
    // zero age is right: the device demonstrably reached us.
    const ageMs = Math.max(0, nowMs - lastSeenAtMs);
    if (ageMs >= offlineAfterMs)
        return 'offline';
    return ageMs >= staleAfterMs ? 'stale' : 'online';
}
export function isOfflineAt(lastSeenAtMs, nowMs, options = {}) {
    return liveConnectivity(lastSeenAtMs, nowMs, options) === 'offline';
}
export function toLiveVehicleView(state, nowMs, options = {}) {
    const tsMs = Date.parse(state.ts);
    const lastSeenAtMs = Number.isNaN(tsMs) ? null : tsMs;
    return {
        ...state,
        connectivity: liveConnectivity(lastSeenAtMs, nowMs, options),
        ageSeconds: lastSeenAtMs === null ? 0 : Math.max(0, Math.round((nowMs - lastSeenAtMs) / 1000)),
    };
}
const KEY_SEGMENT = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * Identifiers are UUIDs everywhere they come from, so anything else means a caller
 * built a key from user input. A ':' slipped into a tenant id would let one tenant
 * address another tenant's key space, which is the multi-tenancy boundary failing
 * silently at the cache layer where no query-level scoping can catch it.
 */
function assertKeySegment(value, what) {
    if (!KEY_SEGMENT.test(value)) {
        throw new Error(`invalid ${what} for a Redis key: ${JSON.stringify(value)}`);
    }
    return value;
}
export const LIVE_STATE_PREFIX = 'fleetcore:live';
export function liveStateKey(tenantId, vehicleId) {
    return `${LIVE_STATE_PREFIX}:${assertKeySegment(tenantId, 'tenantId')}:v:${assertKeySegment(vehicleId, 'vehicleId')}`;
}
/**
 * Sorted set of vehicleId scored by the newest record timestamp in milliseconds.
 * A ZSET rather than a SET so the map can ask for "everything seen since T" in one
 * call, and so offline stays a read-time comparison instead of a stored flag.
 */
export function liveStateIndexKey(tenantId) {
    return `${LIVE_STATE_PREFIX}:${assertKeySegment(tenantId, 'tenantId')}:index`;
}
/**
 * The compare-and-set. KEYS: state hash, tenant index. ARGV: incoming timestamp in
 * millis, serialized state, vehicle id, TTL seconds (0 disables).
 *
 * The comparison is `>=`, not `>`: an equal timestamp is a redelivery of a record
 * already applied (invariant 2 makes that harmless in storage, and it must be
 * harmless here too), and rewriting on equality would let two decoders processing the
 * same redelivered record flap the projection between two serializations of it.
 *
 * Returns 1 when the state was written, 0 when the incoming record was older.
 */
export const LIVE_STATE_CAS_SCRIPT = `
local stored = redis.call('HGET', KEYS[1], 'ts')
if stored and tonumber(stored) >= tonumber(ARGV[1]) then
  return 0
end
redis.call('HSET', KEYS[1], 'ts', ARGV[1], 'state', ARGV[2])
redis.call('ZADD', KEYS[2], ARGV[1], ARGV[3])
local ttl = tonumber(ARGV[4])
if ttl and ttl > 0 then
  redis.call('EXPIRE', KEYS[1], ttl)
end
return 1
`;
/**
 * The same rule as `LIVE_STATE_CAS_SCRIPT`, in TypeScript, for callers that already
 * hold both timestamps and for tests. It is NOT a substitute for the script: two
 * decoders reading, comparing and writing separately will interleave, and the older
 * record wins whenever it is the one that writes second.
 */
export function shouldAcceptLiveUpdate(storedTsMs, incomingTsMs) {
    if (!Number.isFinite(incomingTsMs))
        return false;
    if (storedTsMs === null || storedTsMs === undefined || !Number.isFinite(storedTsMs))
        return true;
    return incomingTsMs > storedTsMs;
}
/**
 * The WebSocket event names. Shared so the server cannot emit an event the client
 * never listens for — the failure mode there is a live map that renders once on load
 * and then never updates, with no error anywhere.
 */
export const WS_EVENTS = {
    /** Client → server: start receiving updates for the caller's tenant. */
    subscribeTenant: 'live:subscribe_tenant',
    /** Client → server: follow one vehicle in detail. */
    subscribeVehicle: 'live:subscribe_vehicle',
    unsubscribeVehicle: 'live:unsubscribe_vehicle',
    /** Server → client: the full set, sent once on subscribe. */
    snapshot: 'live:snapshot',
    /** Server → client: one vehicle moved. */
    vehicleUpdate: 'live:vehicle_update',
    /** Server → client: a vehicle crossed the offline threshold, computed at read time. */
    vehicleConnectivity: 'live:vehicle_connectivity',
    /** Server → client: an alert fired (Phase 3 fills the payload). */
    event: 'live:event',
    /** Server → client: the subscription was refused or dropped. */
    error: 'live:error',
};
/**
 * Rooms are tenant-prefixed, including the per-vehicle room.
 *
 * A room named for the vehicle alone would let a client that guessed a UUID join
 * another tenant's stream, and a socket room join is not a query the tenant-scoping
 * layer ever sees. The server still authorises the join; the prefix means a mistake
 * there cannot be exploited by guessing an id.
 */
export function tenantRoom(tenantId) {
    return `tenant:${assertKeySegment(tenantId, 'tenantId')}`;
}
export function vehicleRoom(tenantId, vehicleId) {
    return `${tenantRoom(tenantId)}:vehicle:${assertKeySegment(vehicleId, 'vehicleId')}`;
}
//# sourceMappingURL=live-state.js.map